import { Box } from '@chakra-ui/react';
import React from 'react';

import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { CARD_BG } from '../../styles/tokens';
import { Slot } from '../Slot';
import { useTabSelection } from '../TabSelectionContext';

/**
 * LEFT-pane document body -- a scrollable region that will hold the active document's content in a
 * future story (`left.viewer.contentId: 'doc.body'`); empty by default, no document content this
 * story. `overflowY="auto"` + `minH={0}` + `flex="1"` is the same "fills remaining space and
 * scrolls independently" contract every pane body follows -- `flex="1"` lets it fill whatever room
 * `DocumentTitleNav`'s fixed height doesn't take in the parent flex column, `minH={0}` overrides
 * the flex-item default `min-height:auto` (which would otherwise let it grow to fit content and
 * defeat scrolling), and a bounded ancestor (`DocumentationPane`'s `h="100%" minH={0}` column) gives
 * this `overflow-y:auto` a definite height to actually scroll within.
 *
 * `overflowX="hidden"` is explicit, NOT the default: setting only `overflowY:auto` leaves
 * `overflow-x:visible`, which the CSS spec then PROMOTES to `auto` (a computed-value rule) -- so the
 * pane would silently scroll HORIZONTALLY whenever its content is even slightly wider than itself
 * (a long unbroken line, a wide table), sliding the whole pane "back and forth". Pinning it `hidden`
 * makes the region vertical-scroll-only; wide content is NOT clipped/lost, because the injected note
 * body (`NoteViewer`'s `note-viewer-body`) carries its OWN `overflowX:auto` (wide tables scroll in
 * place) plus `overflow-wrap:anywhere`/`word-break:break-word` (long text wraps) -- every letter
 * stays reachable, just without the outer pane bouncing.
 *
 * `bg={CARD_BG}` (styles/tokens.ts) makes the white document-body surface
 * explicit rather than relying on inheriting `DocumentationPane`'s own card background.
 *
 * DATUM CONTAINER: the region `<Box>` stays region-only -- `data-content-
 * key` unchanged, NO `data-slot-key` (that attribute is for orderable list ITEMS, not a container's
 * own single bound datum). `viewer` (a `DataContainerMarker`) is itself the `Slot` item, bound to
 * its own `contentId` (`'doc.body'`); when there are no visible tabs at all, the fallback is
 * `() => renderPlainText(value)` (no `?? label` -- a datum container has no `label` to fall back to)
 * -- reproduces the original empty body exactly when `value` is `undefined` (the empty-shell
 * default): `renderPlainText(undefined)` is `null`, which renders nothing.
 *
 * TAB SELECTION: a selected
 * LEFT-pane doc tab's slot renders HERE, in the pane body -- not in `DocumentTitleNav`'s own
 * (deliberately empty) `TabPanel`. `visibleTabs` is the SAME visible-and-order-sorted list
 * `DocumentTitleNav`'s own strip renders from; `selectedTab` looks up the CURRENTLY SELECTED tab
 * (`useTabSelection()`, scoped per pane by the ancestor `TabSelectionProvider`) within that list,
 * falling back to `visibleTabs[0]` -- the SAME tab the controlled strip's `Math.max(0,
 * visibleTabs.findIndex(...))` shows highlighted whenever `selectedContentKey` is `undefined` or
 * stale/hidden (the body must agree with what the strip actually displays, not desync onto
 * the unrelated `viewer` datum). `activeItem` is the resolved `Slot` to render -- the selected/
 * defaulted tab whenever `visibleTabs` is non-empty, else `viewer` itself (the only case left where
 * the datum-marker fallback path applies -- the true "no tabs" empty shell, byte-identical to before
 * this task). The rendered `Slot`'s `fallback` also differs by case: when a tab is
 * selected/defaulted, it falls all the way back to that tab's own `label` (`() =>
 * renderPlainText(value) ?? selectedTab.label`) so a tab with no bound state value shows its label
 * instead of rendering blank; the datum-marker case (no tabs) has no `label` to fall back to, so it
 * stays `() => renderPlainText(value)`.
 *
 * SYMMETRY WITH `ReviewContent`: whenever `selectedTab` is the
 * active item, its `Slot` is wrapped in its own `<Box data-slot-key={selectedTab.contentKey}>` --
 * the same stable-identity wrapper `ReviewContent` already gives its own selected tab's `Slot`. The
 * datum-marker case (`activeItem === viewer`, reachable only when there are no tabs at all) renders
 * the bare `Slot` with no such wrapper -- `data-slot-key` names an orderable list ITEM (the DATUM
 * CONTAINER paragraph above), not a container's own single bound datum, so this preserves both that
 * rule and the empty shell's zero-`data-slot-key` byte-identity (`CodingScreenShell.test.tsx`'s
 * empty-shell regression).
 */
export const ClinicalDocumentViewer: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { viewer } = config.left;
  const { selectedContentKey } = useTabSelection();
  const visibleTabs = config.left.tabs
    .filter((tab) => tab.visible)
    .sort((a, b) => a.order - b.order);
  const selectedTab =
    visibleTabs.find((tab) => tab.contentKey === selectedContentKey) ??
    visibleTabs[0];
  const activeItem = selectedTab ?? viewer;
  const value = readState(state, activeItem.contentId);
  const fallback = selectedTab
    ? () => renderPlainText(value) ?? selectedTab.label
    : () => renderPlainText(value);
  const slotElement = (
    <Slot item={activeItem} value={value} mode={mode} fallback={fallback} />
  );

  return (
    <Box
      role="region"
      aria-label="Document viewer"
      data-content-key={viewer.contentKey}
      overflowY="auto"
      overflowX="hidden"
      minH={0}
      flex="1"
      bg={CARD_BG}
    >
      {selectedTab ? (
        <Box data-slot-key={selectedTab.contentKey}>{slotElement}</Box>
      ) : (
        slotElement
      )}
    </Box>
  );
};
